// ============================================================================
// HISTORICAL PRICING LOCK
//
// Ugo's T&Cs form the price contract at the moment of booking. When B2C rates
// below change, reservations booked before the change must still be settled
// (consuntivo) using the rates that were live when they were booked.
//
// STAGING TEST — currently 2026-07-31T00:00:00Z to validate the
// historical-pricing-lock cutoff end-to-end on staging (see ugo-turbo's
// docs/b2c-cutoff-staging-test-plan.md and -test-matrix.it.md). The value is
// committed to develop on purpose: that is how it reaches the staging
// deployment. Reset it to the placeholder / real go-live date when the test
// window closes.
//
// Must stay equal to the two sibling constants at all times — a mismatch makes
// the same reservation resolve to different pricing eras depending on which
// repo computes it:
//   - libs/quote-calculations/src/lib/fees.ts (this repo)
//   - ugo-turbo packages/calculations/src/constants.ts
export const NEW_PRICING_CUTOFF_DATE = new Date('2026-07-31T00:00:00Z');

// Current B2C rates, mirroring ugo-turbo's packages/calculations/src/constants.ts.
export const B2C_KM_RATE = 0.5;
export const B2C_HOURLY_RATE_TIER_1 = 18;
export const B2C_HOURLY_RATE_TIER_2 = 16;
export const B2C_HOURLY_RATE_TIER_1_LAST_HOUR = 6;
export const PHONE_ASSISTANCE_PRICE_PER_HALF_HOUR = 6;
export const PHONE_ASSISTANCE_MIN_HOURS = 1;
export const CITY_MIN_DISTANCE: Record<string, number> = { roma: 10 };
export const CITY_MIN_DISTANCE_DEFAULT = 5;
export const KASKO_PER_50KM = 2.5;

/**
 * True legacy (pre-refactor, production) B2C rates — verified against git
 * history (`4af7d5f^`, the last commit before the B2C pricing refactor
 * that's live on staging only), NOT a copy of the current values above.
 * Production hasn't deployed the new pricing yet, so it still runs on these
 * legacy rates until NEW_PRICING_CUTOFF_DATE is set to the real production
 * go-live date. DO NOT "helpfully" sync these to the current values —
 * they must stay frozen at the pre-refactor numbers.
 *
 * NOTE: hourly and errands pricing changed FORMULA, not just numbers (see
 * utils.ts and caregiving-errands-strategy.ts) — LEGACY_B2C_HOURLY_RATE_BASE
 * and the errands constants below feed a genuinely different algorithm, not
 * a swapped-in value within the current tiered/unified shape.
 */
export const LEGACY_B2C_KM_RATE = 0.5;
export const LEGACY_B2C_HOURLY_RATE_BASE = 15;
export const LEGACY_B2C_MIN_HOURLY_RATE = 8;
export const LEGACY_ERRANDS_FIRST_HOUR_COST = 12.5;
export const LEGACY_ERRANDS_SUBSEQUENT_PRICE_PER_HALF_HOUR = 6;
export const LEGACY_PHONE_ASSISTANCE_PRICE_PER_HALF_HOUR = 5;
export const LEGACY_PHONE_ASSISTANCE_MIN_HOURS = 0.5;
export const LEGACY_CITY_MIN_DISTANCE: Record<string, number> = { roma: 10 };
export const LEGACY_CITY_MIN_DISTANCE_DEFAULT = 5;
export const LEGACY_KASKO_PER_50KM = 2.5;

/**
 * True if `reservationCreationDate` (the reservation's booking timestamp) falls before
 * the new-pricing go-live — i.e. this reservation's consuntivo must use the
 * legacy rates/formulas above. Omit `reservationCreationDate` for brand-new quotes,
 * which should always use current rates.
 */
export function isLegacyPricing(reservationCreationDate?: Date): boolean {
  return (
    !!reservationCreationDate &&
    reservationCreationDate < NEW_PRICING_CUTOFF_DATE
  );
}

export interface B2CRates {
  B2C_KM_RATE: number;
  PHONE_ASSISTANCE_PRICE_PER_HALF_HOUR: number;
  PHONE_ASSISTANCE_MIN_HOURS: number;
  CITY_MIN_DISTANCE: Record<string, number>;
  CITY_MIN_DISTANCE_DEFAULT: number;
  KASKO_PER_50KM: number;
}

/**
 * Resolves the B2C rates that are simple swapped values within an unchanged
 * formula shape (km rate, kasko, city min distance, phone assistance).
 * Hourly and errands pricing are NOT part of this — their formula shape
 * itself differs between legacy and current, so they branch directly on
 * `isLegacyPricing()` in utils.ts / caregiving-errands-strategy.ts instead.
 */
export function resolveB2CRates(reservationCreationDate?: Date): B2CRates {
  return isLegacyPricing(reservationCreationDate)
    ? {
        B2C_KM_RATE: LEGACY_B2C_KM_RATE,
        PHONE_ASSISTANCE_PRICE_PER_HALF_HOUR:
          LEGACY_PHONE_ASSISTANCE_PRICE_PER_HALF_HOUR,
        PHONE_ASSISTANCE_MIN_HOURS: LEGACY_PHONE_ASSISTANCE_MIN_HOURS,
        CITY_MIN_DISTANCE: LEGACY_CITY_MIN_DISTANCE,
        CITY_MIN_DISTANCE_DEFAULT: LEGACY_CITY_MIN_DISTANCE_DEFAULT,
        KASKO_PER_50KM: LEGACY_KASKO_PER_50KM,
      }
    : {
        B2C_KM_RATE,
        PHONE_ASSISTANCE_PRICE_PER_HALF_HOUR,
        PHONE_ASSISTANCE_MIN_HOURS,
        CITY_MIN_DISTANCE,
        CITY_MIN_DISTANCE_DEFAULT,
        KASKO_PER_50KM,
      };
}
